import { IFormContext } from "@multiweb/types/multiweb";
import { IDropDownMenuItem } from "UI/UI.Controls/DropDownMenu/DropDownInterfaces";
import { Event } from "../../../../../Global/Base/Events";
import { FilterBlockAttrNameToDataMap, IFilterBlock } from "./FilterInterfaces";
import { IFilterExecutedEventArgs } from "./FilterManager";
import { FilterPanel } from "./FilterPanel";

export type FilterManagerColumn = {
    locKey: string;
    locParams?: string[];
    locText?: string;
    attrName: string;
    dataType: string;
    displayFormat?: string;
    canFilterNull: boolean;
    canFilterCompare: boolean;
    canFilterEqual: boolean;
    canFilterLike: boolean;
};

/**
 * Třída starající se na daném framu o filtraci, obsahuje seznam vsech filtru daneho framu
 */
export interface IFilterManager {
     /** Událost vyvolaná manažerem při stisku na tlačítko filtrovat */
     readonly filterExecuted: Event<IFilterManager, IFilterExecutedEventArgs>;

     /** Událost vyvolaná při čištění filtrů */
     readonly filterCleared: Event<IFilterManager, string>;

     /** Událost volaná při vytvoření nového filtru */
     readonly filterCreated: Event<IFilterManager, IFilterBlock>;

     /** Událost volaná při zvolení filtru */
     readonly filterChanged: Event<IFilterManager, IFilterBlock>;

     /** Událost volaná při uložení filtru */
     readonly filterSaveStateChanged: Event<IFilterManager, IFilterBlock>;

     /** Událost volaná při změně podmínky na frame */
     readonly frameConditionChanged: Event<IFilterManager, string>;

     /** Událost volaná při změně pokročilého filtru */
     readonly advancedConditionChanged: Event<IFilterManager, string>;

     /** Kontext formuláře */
     formContext: IFormContext;
     /** Obsahuje informace o přihlášeném uživateli z důvodu ukládání filtrů */
     className: string;
     /*** Seznam, obsahující jako key AttrName a hodnotu Datovy typ a lokalizační klíč pro seznam sloupců */
     attrNameToDataType: FilterBlockAttrNameToDataMap;

     readonly frameName: string;
     frameCondition: string;

     /** Aktuálně použitý filtr */
     readonly currentFilter: IFilterBlock;

     /** Zda je nastaven pokročilý filtr */
     readonly hasActiveAdvancedFilter: boolean;

     readonly canEditImplementationFilters: boolean;

     init(): Promise<void>;

     /**
      * Vrací použitý FilterPanel
      */
     readonly filterPanel: FilterPanel;

     /**
      * Vrátí aktuálně použitý filtr aktivním průchodem spravovaných filtrů a výběrem použitého
      */
     getCurrentFilter(): IFilterBlock;

     /**
      * Vybere filtr
      * @param filter FilterBlock který se má vybrat
      * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
      */
     selectFilter(filter: IFilterBlock, addToFilterPanel?: boolean): void;

     /**
      * Vybere filtr, aktualizuje stav filtrovacího panelu a spustí filtrování
      * @param filter FilterBlock který se má vybrat
      * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
      */
     selectAndApplyFilter(filterBlock: IFilterBlock, addToFilterPanel?: boolean): void ;

     /**
      * Vybere filtr podle jeho identifikátoru
      * @param filterId Identifikátor filtru, který se má vybrat. Pokud je null, zruší se filtrování
      * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
      * @returns Pokud byl nalezen filtr s daným identifikátorem, vrací se jeho FilterBlock; Jinak null
      */
     selectFilterById(filterId: string | null, addToFilterPanel?: boolean): IFilterBlock | null;

     /**
      * Vybere filtr podle jeho identifikátoru, aktualizuje stav filtrovacího panelu a spustí filtrování
      * @param filterId Identifikátor filtru, který se má vybrat. Pokud je null, zruší se filtrování
      * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
      * @returns Pokud byl nalezen filtr s daným identifikátorem, vrací se jeho FilterBlock; Jinak null
      */
     selectAndApplyFilterById(filterId: string | null, addToFilterPanel?: boolean): IFilterBlock | null;

     /**
      * Vrátí seznam filtrů
      */
     getFilterList(): Promise<Map<string, IFilterBlock>>;

     /** Kontrola existence názvu filtru v seznamu filtrů */
     doesFilterNameExist(filterName: string): Promise<boolean>;

     /**
      * Nacteni ulozenych filtru ze služby
      */
     loadSavedFilters(): Promise<void>;

     /**
      * Nacte data filtru ze serveru. Pokud uz je filtr v seznamu je prepsan.
      * @param id ID Objekut
      */
     getSavedFilterById(id: string): Promise<IFilterBlock>;

     /**
      * Výtvoří nový prázdný filtrační blok
      * @param selected pokud se ma automaticky po vytvoření vybrat
      */
     createNew(selected: boolean): IFilterBlock;

     /**
      * Zobrazí vstup pro vložení dříve zkopírovaného fitru
      */
     insertFilter(filter?: string): void;

     /** Zobrazí ve filtrovacím panelu vstup pro pokročilý filtr */
     showAdvancedFilter(): void;

     /** Aktualizuje v panelu stav filtrů */
     updateFilterPanel(): void;

     /**
      * vyčistí filtry (zruší vybrany filtr a nahraje prazdné pole)
      */
     clearFilter(): void ;

     clearValueListFilters(): void;
     /**
      * spustí filtrování
      */
     execute(): void;

     /**
      * Trvale odstraní požadovaný filtr
      * @param filterBlock Požadovaný filtr
      */
     removeFilter(block: IFilterBlock): Promise<void>;

     /**
      * Vytvoří položku menu pro menu s filtry
      * @param filterBlock Blok filtrů
      * @param emptyBtn Tlačítko "Vše" pro zrušení jeho výběru po aktivaci uloženého filtru
      */
     createFilterMenuItem(filterBlock: IFilterBlock, emptyBtn: IDropDownMenuItem): Promise<IDropDownMenuItem>;

     /**
      * Vrací název filtru upravený podle jeho aktuálního stavu
      * @param block Blok filtru, který udržuje jeho stav
      */
     getFilterCaption(block: IFilterBlock): Promise<string> ;

     /**
      * Vrací název filtru upravený podle toho, jestli je implementační
      * @param block Blok filtru
      */
     getFilterDisplayName(block: IFilterBlock): Promise<string> ;
}
